import {CheckCircle2Icon, Plus, Trash2, FileUp} from "lucide-react";
import React, {useState, useEffect} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {createQuiz} from "../../utils/question";
import {apiRequest, apiRequestWithAuth} from "../../utils/api";
import {useAppSelector} from "../../store/hook";
import {title} from "process";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store/store";
import {addExamRequest} from "../../slices/examSlice";
import {ApiResponse} from "../../types";
import {call} from "redux-saga/effects";
import {fetchUsersStart} from "../../slices/userSlice";
import {Table, TableBody, TableCell, TableHeader, TableRow} from "../ui/table";
import {EditIcon} from "../../icons";

interface Exam {
    id: number;
    title: string;
    shortDescription: string;
    categoryId: string;
    skillId: string;
    isMockTest: boolean;
    statusText?: string;
    createTime: string;
    timeLimit: number;
    skill: string;
    category?: {
        id: number;
        title: string;
    };
    author?: {
        id: number;
        name: string;
    };
}


const SKILL_OPTIONS = [
    { value: "", label: "Tất cả" },
    { value: "1", label: "Grammar & Vocabulary" },
    { value: "2", label: "Listening" },
    { value: "3", label: "Writing" },
    { value: "4", label: "Reading" },
    { value: "5", label: "Speaking" },
    { value: "6", label: "Reading and Writing" },
    { value: "7", label: "听力 (Nghe)" },
    { value: "8", label: "阅读 (Đọc)" },
    { value: "9", label: "书写 (Viết)" },
];

const ExamsListBuilder = () => {

    const [searchParams, setSearchParams] = useSearchParams();
    const [pageId, setPage] = useState(() => {
        return parseInt(searchParams.get('page') || '1', 10);
    });
    const [hasResult, setHasResult] = useState(true);

    const [exams, setExams] = useState<Exam[]>([]);
    const [isAdding, setIsAdding] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const navigate = useNavigate();

    // Filter states
    const [keyword, setKeyword] = useState(searchParams.get('keyword') || '');
    const [categoryId, setCategoryId] = useState(searchParams.get('categoryId') || '');
    const [isMockTest, setIsMockTest] = useState(searchParams.get('isMockTest') || '');
    const [skillId, setSkillId] = useState(searchParams.get('skillId') || '');
    const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);

    const token = useAppSelector((state) => state.auth.token);

    const dispatch = useDispatch();
    const {exam, loading} = useSelector((state: RootState) => state.exam);

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchExams();
    }, [pageId]);

    const fetchCategories = async () => {
        try {
            const res = await apiRequestWithAuth('/category', 'GET', {});
            if (res.messageCode === 200) {
                const rawCategories = res.result || [];

                // Gom nhóm theo parentId (giống Header.tsx)
                const mapByParent: Record<string, any[]> = {};
                rawCategories.forEach((cat: any) => {
                    const pid = cat.parentId ?? "root";
                    if (!mapByParent[pid]) mapByParent[pid] = [];
                    mapByParent[pid].push(cat);
                });

                // Build tree phẳng + prefix đệ quy
                const buildHierarchy = (parentId: string = "root", prefix: string = ""): { value: string; label: string }[] => {
                    const children = mapByParent[parentId] || [];
                    return children.flatMap((child: any) => [
                        { value: child.id.toString(), label: prefix + child.title },
                        ...buildHierarchy(child.id.toString(), prefix + "-- "),
                    ]);
                };

                setCategories(buildHierarchy());
            }
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    };

    const handlePageChange = (newPage: React.SetStateAction<number>) => {
        setPage(newPage);
        setSearchParams({ page: newPage.toString() });
    };

    const buildQuery = (page: number) => {
        const params: Record<string, string> = { page: page.toString() };
        if (keyword) params.keyword = keyword;
        if (categoryId) params.categoryId = categoryId;
        if (isMockTest !== '') params.isMockTest = isMockTest;
        if (skillId) params.skillId = skillId;
        return new URLSearchParams(params).toString();
    };

    const fetchExams = async () => {
        try {
            const response: ApiResponse<any> = await apiRequestWithAuth('/quiz?' + buildQuery(pageId), 'GET', {});
            if (response.messageCode === 200) {
                setExams(response.result);
                if (response.result.length === 20) {
                    setHasResult(true);
                } else {
                    setHasResult(false);
                }
            } else {
                setHasResult(false);
                throw new Error("Failed to fetch exams");
            }
        } catch (err) {
            console.error("Error fetching exams:", err);
        }
    };

    const handleSearch = () => {
        const params: Record<string, string> = { page: '1' };
        if (keyword) params.keyword = keyword;
        if (categoryId) params.categoryId = categoryId;
        if (isMockTest !== '') params.isMockTest = isMockTest;
        if (skillId) params.skillId = skillId;
        setSearchParams(params);
        setPage(1);
        fetchExams();
    };

    const handleReset = () => {
        setKeyword('');
        setCategoryId('');
        setIsMockTest('');
        setSkillId('');
        setSearchParams({ page: '1' });
        setPage(1);
        setTimeout(fetchExams, 0);
    };

    const addExam = async () => {
        try {
            const newExam = {
                title: "New Exam",
                shortDescription: "This is a newly created exam",
                categoryId: 3,
            };

            const response: ApiResponse<any> = await apiRequestWithAuth(`/quiz`, "POST", newExam);

            const examId = response.result.id;
            navigate(`/exams/${examId}/update`);
        } catch (err: any) {
            console.error("Error saving exam:", err);
        } finally {
        }
    };

    const deleteExam = async (examId: number) => {
        if (!window.confirm("Bạn có chắc chắn muốn xóa bài thi này?")) {
            return;
        }

        setDeletingId(examId);
        try {
            const response: ApiResponse<any> = await apiRequestWithAuth(`/quiz/${examId}`, "DELETE", {});

            if (response.messageCode === 200) {
                // Remove exam from local state
                setExams(prev => prev.filter(exam => exam.id !== examId));
                // Optional: Show success message
                alert("Xóa bài thi thành công!");
            } else {
                throw new Error("Failed to delete exam");
            }
        } catch (err: any) {
            console.error("Error deleting exam:", err);
            alert("Có lỗi xảy ra khi xóa bài thi!");
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) return <p>Loading exams...</p>;

    return (
        <>
            {/* Action buttons - trên filter */}
            <div className="mb-4 flex gap-3">
                <button
                    className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300"
                    onClick={addExam}
                >
                    + Thêm bài thi
                </button>
                <button
                    className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 flex items-center gap-2"
                    onClick={() => navigate('/exams/import')}
                >
                    <FileUp className="w-4 h-4" />
                    Import từ Word
                </button>
            </div>

            {/* Filter Bar */}
            <div className="mb-4 p-4 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.05] rounded-xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                    {/* Keyword */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Từ khóa</label>
                        <input
                            type="text"
                            value={keyword}
                            onChange={e => setKeyword(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            placeholder="Tìm kiếm..."
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Danh mục</label>
                        <select
                            value={categoryId}
                            onChange={e => setCategoryId(e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        >
                            <option value="">Tất cả danh mục</option>
                            {categories.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Type (skillId) */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Type</label>
                        <select
                            value={skillId}
                            onChange={e => setSkillId(e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        >
                            {SKILL_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* isMockTest */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Thi thử</label>
                        <select
                            value={isMockTest}
                            onChange={e => setIsMockTest(e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        >
                            <option value="">Tất cả</option>
                            <option value="1">Có</option>
                            <option value="0">Không</option>
                        </select>
                    </div>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={handleSearch}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition"
                    >
                        Tìm kiếm
                    </button>
                    <button
                        onClick={handleReset}
                        className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium py-2 px-4 rounded-lg transition"
                    >
                        Reset
                    </button>
                </div>
            </div>

            <div
                className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
                <div className="max-w-full overflow-x-auto">
                    <div className="min-w-[1102px]">
                        <Table>
                            {/* Table Header */}
                            <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                                <TableRow>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-center text-theme-xs dark:text-gray-400"
                                    >
                                        ID
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                    >
                                        Tên bài thi
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-nowrap text-start text-theme-xs dark:text-gray-400"
                                    >
                                        Danh mục
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                    >
                                        Type
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-nowrap text-center text-theme-xs dark:text-gray-400"
                                    >
                                        Thi thử
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                    >
                                        Người tạo
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-nowrap text-center text-theme-xs dark:text-gray-400"
                                    >
                                        Trạng thái
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-nowrap text-start text-theme-xs dark:text-gray-400"
                                    >
                                        Ngày tạo
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                    >
                                    </TableCell>
                                </TableRow>
                            </TableHeader>

                            {/* Table Body */}
                            <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                                {exams?.map((exam) => (
                                    <TableRow key={exam.id}>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-center text-theme-sm dark:text-gray-400">
                                            {exam.id}
                                        </TableCell>
                                        <TableCell className="px-5 py-4 sm:px-6 text-start">
                                            <div className="flex items-center gap-3">
                                                <div>
                                                <span
                                                    className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                    {exam.title}
                                                </span>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {exam?.category?.title}
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {exam.skill}
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-center text-theme-sm dark:text-gray-400">
                                            {exam.isMockTest ? (
                                                <>
                                                    <CheckCircle2Icon className="w-5 h-5 m-auto text-success-500" />
                                                </>
                                            ) : null }
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {exam?.author?.name}
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-center text-theme-sm dark:text-gray-400">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-0.5 justify-center gap-1 rounded-full font-medium text-theme-xs
                                                ${exam.statusText == 'Active' ? 'bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500' : 'bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-500'}`}>
                                              {exam.statusText}
                                            </span>
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {exam.createTime}
                                        </TableCell>
                                        <TableCell
                                            className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400 text-nowrap">
                                            <button onClick={() => navigate(`/exams/${exam.id}/update`)}
                                                    className="text-sm px-1.5 py-1.5 mr-2 rounded-md bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition">
                                                <EditIcon />
                                            </button>
                                            <button
                                                onClick={() => deleteExam(exam.id)}
                                                disabled={deletingId === exam.id}
                                                className="text-sm px-1.5 py-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Xóa"
                                            >
                                                {deletingId === exam.id ? (
                                                    <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                                ) : (
                                                    <Trash2 className="w-4 h-4" />
                                                )}
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <div className="p-4 border-t border-gray-100 dark:border-gray-800 sm:p-6">
                            <div className="space-y-6">
                                <div className="flex items-center justify-between gap-8 sm:justify-between">
                                    <button
                                        disabled={pageId === 1}
                                        onClick={() => handlePageChange(pageId - 1)}
                                        className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200 sm:px-3.5 sm:py-2.5"
                                    >
                                        <svg className="fill-current" width="20" height="20" viewBox="0 0 20 20"
                                             fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" clipRule="evenodd"
                                                  d="M2.58203 9.99868C2.58174 10.1909 2.6549 10.3833 2.80152 10.53L7.79818 15.5301C8.09097 15.8231 8.56584 15.8233 8.85883 15.5305C9.15183 15.2377 9.152 14.7629 8.85921 14.4699L5.13911 10.7472L16.6665 10.7472C17.0807 10.7472 17.4165 10.4114 17.4165 9.99715C17.4165 9.58294 17.0807 9.24715 16.6665 9.24715L5.14456 9.24715L8.85919 5.53016C9.15199 5.23717 9.15184 4.7623 8.85885 4.4695C8.56587 4.1767 8.09099 4.17685 7.79819 4.46984L2.84069 9.43049C2.68224 9.568 2.58203 9.77087 2.58203 9.99715C2.58203 9.99766 2.58203 9.99817 2.58203 9.99868Z"
                                                  fill=""></path>
                                        </svg>
                                        <span className="hidden sm:inline"> Previous </span>
                                    </button>
                                    <button
                                        disabled={!hasResult}
                                        onClick={() => handlePageChange(pageId + 1)}
                                        className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200 sm:px-3.5 sm:py-2.5">
                                        <span className="hidden sm:inline"> Next </span>
                                        <svg className="fill-current" width="20" height="20" viewBox="0 0 20 20"
                                             fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" clipRule="evenodd"
                                                  d="M17.4165 9.9986C17.4168 10.1909 17.3437 10.3832 17.197 10.53L12.2004 15.5301C11.9076 15.8231 11.4327 15.8233 11.1397 15.5305C10.8467 15.2377 10.8465 14.7629 11.1393 14.4699L14.8594 10.7472L3.33203 10.7472C2.91782 10.7472 2.58203 10.4114 2.58203 9.99715C2.58203 9.58294 2.91782 9.24715 3.33203 9.24715L14.854 9.24715L11.1393 5.53016C10.8465 5.23717 10.8467 4.7623 11.1397 4.4695C11.4327 4.1767 11.9075 4.17685 12.2003 4.46984L17.1578 9.43049C17.3163 9.568 17.4165 9.77087 17.4165 9.99715C17.4165 9.99763 17.4165 9.99812 17.4165 9.9986Z"
                                                  fill=""></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ExamsListBuilder;
